import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaUser, FaLock, FaEnvelope, FaArrowRight, FaCheck } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  overflow: hidden;
`;

const LeftPanel = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #222, #444);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  padding: 40px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled.div`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
`;

const WelcomeText = styled.h1`
  font-size: 36px;
  margin-bottom: 20px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 16px;
  max-width: 400px;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 40px;
  opacity: 0.8;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  padding: 40px;
  overflow-y: auto;
`;

const RegisterForm = styled.form`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 10px;
  padding: 40px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  margin: 20px 0;
`;

const FormTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 30px;
  color: #333;
  text-align: center;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 25px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 15px 15px 50px;
  border: 1px solid ${props => props.error ? '#e74c3c' : '#eee'};
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s;
  
  &:focus {
    border-color: #333;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
    outline: none;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const Button = styled.button`
  width: 100%;
  padding: 15px;
  background-color: #222;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover {
    background-color: #000;
  }
  
  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
  
  svg {
    margin-left: 8px;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  margin-bottom: 20px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #27ae60;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 10px;
  background-color: #e8f8f0;
  border-radius: 6px;
  text-align: center;
`;

const PasswordRequirements = styled.div`
  margin-top: 5px;
  margin-bottom: 15px;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${props => props.met ? '#27ae60' : '#666'};
  margin: 3px 0;
  
  svg {
    margin-right: 5px;
    font-size: 10px;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #666;
  
  a {
    color: #222;
    font-weight: 500;
    margin-left: 5px;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const { signUp, error, clearError, isAuthenticated } = useAuth();
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  // Update form error if auth context has an error
  useEffect(() => {
    if (error) {
      setFormError(error);
    }
  }, [error]);
  
  // Password strength checks
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isPasswordValid = hasMinLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar;
  const passwordsMatch = password === confirmPassword;
  
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      setFormError('Please fill out all fields');
      return;
    }
    
    if (!isPasswordValid) {
      setFormError('Password does not meet all requirements');
      return;
    }
    
    if (!passwordsMatch) {
      setFormError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setFormError('');
    setSuccessMessage('');
    clearError();
    
    try {
      await signUp(email, password, name);
      setSuccessMessage('Account created successfully! You can now sign in.');
      
      // Reset form
      setName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      console.error('Register error:', err);
      
      // Display specific form errors
      switch (err.code) {
        case 'auth/email-already-in-use':
          setFormError('This email is already in use');
          break;
        case 'auth/invalid-email':
          setFormError('Invalid email format');
          break;
        case 'auth/weak-password':
          setFormError('Password is too weak');
          break;
        default:
          if (!formError) {
            setFormError('Failed to create account. Please try again.');
          }
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <LeftPanel>
        <Logo>Godai Studios</Logo>
        <WelcomeText>Admin Portal</WelcomeText>
        <Description>
          Join our admin team to manage products, workshops, orders, and more from the central administration dashboard.
        </Description>
      </LeftPanel>
      <RightPanel>
        <RegisterForm onSubmit={handleRegister}>
          <FormTitle>Create Admin Account</FormTitle>
          
          {formError && <ErrorMessage>{formError}</ErrorMessage>}
          {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
          
          <InputGroup>
            <InputIcon>
              <FaUser />
            </InputIcon>
            <Input 
              type="text" 
              placeholder="Full Name" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={formError.includes('name')}
            />
          </InputGroup>
          
          <InputGroup>
            <InputIcon>
              <FaEnvelope />
            </InputIcon>
            <Input 
              type="email" 
              placeholder="Email Address" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={formError.includes('email')}
            />
          </InputGroup>
          
          <InputGroup>
            <InputIcon>
              <FaLock />
            </InputIcon>
            <Input 
              type="password" 
              placeholder="Password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={formError.includes('password')}
            />
          </InputGroup>
          
          <PasswordRequirements>
            <RequirementItem met={hasMinLength}>
              {hasMinLength && <FaCheck />} At least 8 characters
            </RequirementItem>
            <RequirementItem met={hasUppercase}>
              {hasUppercase && <FaCheck />} One uppercase letter
            </RequirementItem>
            <RequirementItem met={hasLowercase}>
              {hasLowercase && <FaCheck />} One lowercase letter
            </RequirementItem>
            <RequirementItem met={hasNumber}>
              {hasNumber && <FaCheck />} One number
            </RequirementItem>
            <RequirementItem met={hasSpecialChar}>
              {hasSpecialChar && <FaCheck />} One special character
            </RequirementItem>
          </PasswordRequirements>
          
          <InputGroup>
            <InputIcon>
              <FaLock />
            </InputIcon>
            <Input 
              type="password" 
              placeholder="Confirm Password" 
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!passwordsMatch && confirmPassword.length > 0}
            />
          </InputGroup>
          
          <Button type="submit" disabled={loading || !isPasswordValid || !passwordsMatch}>
            {loading ? 'Creating Account...' : 'Create Account'} {!loading && <FaArrowRight />}
          </Button>
          
          <LoginLink>
            Already have an account?
            <Link to="/login">Sign In</Link>
          </LoginLink>
        </RegisterForm>
      </RightPanel>
    </Container>
  );
};

export default Register; 